/**
 * Project Name: Brainsie eCommerce v4
 * Component: Full Vertical Image
 * Description: Full height image component with contained object fit
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Box } from "@mui/material";

export default function FullVerticalImage({
  component,
  section,
  darkMode,
  theme,
}) {
  // Get component data
  const primaryText = component.Primary_Text || "";
  const secondaryText = component.Secondary_Text || "";
  const descriptiveText = component.Descriptive_Text || "";

  // Try to parse settings from Component_Settings
  const componentSettings = component.Component_Settings
    ? component.Component_Settings.startsWith("{")
      ? JSON.parse(component.Component_Settings)
      : {}
    : {};

  // Get image URL - handle Media_Files as string array
  let imageUrl = null;

  console.log("FullVerticalImage Media_Files:", component.Media_Files);

  if (component.Media_Files) {
    let mediaFile;

    if (Array.isArray(component.Media_Files)) {
      // Already an array
      mediaFile = component.Media_Files[0];
    } else if (typeof component.Media_Files === "string") {
      try {
        // Try to parse as JSON array - handle the format ["file.jpg"]
        const parsed = JSON.parse(component.Media_Files);
        mediaFile = Array.isArray(parsed) ? parsed[0] : parsed;
      } catch {
        // If not JSON, treat as direct file path
        mediaFile = component.Media_Files;
      }
    }

    if (mediaFile) {
      // Clean up the media file path - remove any leading/trailing whitespace
      mediaFile = mediaFile.trim();

      // Construct URL with BASEURL
      const baseUrl = process.env.BASEURL || "";

      // Handle absolute URLs
      if (mediaFile.startsWith("http")) {
        imageUrl = mediaFile;
      } else {
        // Ensure proper URL construction
        if (baseUrl.endsWith("/") && mediaFile.startsWith("/")) {
          imageUrl = baseUrl + mediaFile.substring(1);
        } else if (!baseUrl.endsWith("/") && !mediaFile.startsWith("/")) {
          imageUrl = baseUrl + "/" + mediaFile;
        } else {
          imageUrl = baseUrl + mediaFile;
        }
      }
    }

    console.log("🖼️ FullVerticalImage Debug:", {
      component: component,
      mediaFiles: component.Media_Files,
      processedImageUrl: imageUrl,
      baseUrl: process.env.BASEURL,
    });
  } else if (primaryText && primaryText.startsWith("http")) {
    imageUrl = primaryText;
  }

  const showFallback = !imageUrl;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        mb: componentSettings.marginBottom || 2,
        mt: componentSettings.marginTop || 0,
      }}
    >
      {showFallback ? (
        <Box
          sx={{
            width: "100%",
            height: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: darkMode
              ? theme.palette.grey[800]
              : theme.palette.grey[200],
            color: darkMode ? theme.palette.grey[400] : theme.palette.grey[600],
            fontSize: "1rem",
            fontWeight: 500,
            borderRadius: "8px",
          }}
        >
          No Image Available
        </Box>
      ) : (
        <Box
          component="img"
          src={imageUrl}
          alt={
            secondaryText && secondaryText !== "NA"
              ? secondaryText
              : "Full vertical image"
          }
          sx={{
            width: componentSettings.maxWidth || "100%",
            height: componentSettings.height || "auto",
            maxHeight: componentSettings.maxHeight || "none",
            minHeight: componentSettings.minHeight || "200px",
            objectFit: componentSettings.objectFit || "contain",
            borderRadius: componentSettings.borderRadius || 2,
            boxShadow:
              componentSettings.boxShadow || "0 4px 12px rgba(0,0,0,0.1)",
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: componentSettings.hoverScale
                ? `scale(${componentSettings.hoverScale})`
                : "scale(1.02)",
            },
            // Ensure image maintains aspect ratio
            aspectRatio: componentSettings.aspectRatio || "auto",
            ...componentSettings.customStyles,
          }}
          onError={(e) => {
            console.error("🚨 FullVerticalImage load error:", e.target.src);
          }}
        />
      )}
    </Box>
  );
}
